import { Application } from "@hotwired/stimulus";

const application = Application.start();
const controllerLoaders = import.meta.glob("./*_controller.js");
const loadedControllers = new Set();
const loadingControllers = new Map();

const pathFromIdentifier = (identifier) =>
  `./${identifier.replace(/-/g, "_")}_controller.js`;

const identifiersFromDataController = (value) =>
  value
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

const loadController = async (identifier) => {
  if (loadedControllers.has(identifier)) return;
  if (loadingControllers.has(identifier)) {
    await loadingControllers.get(identifier);
    return;
  }

  const loader = controllerLoaders[pathFromIdentifier(identifier)];
  if (!loader) return;

  const loadPromise = loader()
    .then((module) => {
      if (!loadedControllers.has(identifier)) {
        application.register(identifier, module.default);
        loadedControllers.add(identifier);
      }
    })
    .finally(() => {
      loadingControllers.delete(identifier);
    });

  loadingControllers.set(identifier, loadPromise);
  await loadPromise;
};

const collectControllerValues = (root) => {
  const values = [];
  if (!(root instanceof Element)) return values;

  if (root.hasAttribute("data-controller")) {
    values.push(root.getAttribute("data-controller"));
  }

  root.querySelectorAll("[data-controller]").forEach((element) => {
    values.push(element.getAttribute("data-controller"));
  });

  return values.filter(Boolean);
};

const loadControllersInTree = async (root) => {
  const identifiers = collectControllerValues(root).flatMap(
    identifiersFromDataController,
  );
  await Promise.all(identifiers.map((identifier) => loadController(identifier)));
};

void loadControllersInTree(document.documentElement);

const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === "attributes") {
      void loadControllersInTree(mutation.target);
      return;
    }

    mutation.addedNodes.forEach((node) => {
      void loadControllersInTree(node);
    });
  });
});

observer.observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ["data-controller"],
});

export { application };
